<template>
  <b-modal
    ref="bModalSyncProduct"
    size="lg"
    dialog-class="!max-w-[600px]"
    body-class="!p-[32px]"
    centered
    hide-footer
    hide-header
    @hidden="onModalHidden"
  >
    <h4 class="text-center text-[20px] font-semibold mb-[24px] text-black">
      Hubungkan Produk
    </h4>

    <step-indicator
      ref="stepIndicator"
      :schema="stepSchema"
    />

    <sync-info-card
      :product-name="item.product_name"
      :variant-name="item.variant_name"
    />

    <b-input-group class="mb-[12px] !rounded-[8px]">
      <b-input-group-prepend is-text>
        <span class="k-search-normal-1 text-[20px]" />
      </b-input-group-prepend>
      <b-form-input
        v-model="searchedProduct"
        type="search"
        placeholder="Cari produk"
        class="mb-[12px] !border-l-0 !pl-0"
        @input="handleSearch"
      />
    </b-input-group>

    <b-table
      :items="tableData"
      :fields="tableFields(type)"
      class="rounded-[8px] border-[1px] border-[#E2E2E2]"
      responsive
      sticky-header
      show-empty
    >
      <template #empty>
        <div class="flex items-center justify-center h-full my-[12px] gap-[8px]">
          <b-spinner
            v-if="productsFetcher && productsFetcher.isLoading"
            small
            variant="primary"
          />
          <span class="text-[#828282] text-[14px] font-[400]">
            {{ tableEmptyText }}
          </span>
        </div>
      </template>

      <template
        #custom-foot
      >
        <td
          v-show="shouldShowLoadMore"
          ref="loadingFooter"
          colspan="100%"
        >
          <div class="flex items-center justify-center h-full my-[12px] gap-[8px]">
            <b-spinner
              small
              variant="primary"
            />
            <span class="text-[#828282] text-[14px] font-[400]">
              memuat produk lainnya...
            </span>
          </div>
        </td>
      </template>

      <template #cell(action)="data">
        <BtnConnectProduct
          :ref="`btnConnectProduct-${data.index}`"
          :disabled="isSyncing"
          @click="handleSyncAction(data)"
        >
          Hubungkan
        </BtnConnectProduct>
      </template>
    </b-table>
  </b-modal>
</template>

<script>
import useDebouncedSearch from '../integrasi-marketplace/products-mapping/composables/useDebouncedSearch'
import useIntersectionObserver from '../integrasi-marketplace/products-mapping/composables/useIntersectionObserver'
import useProductList from '../integrasi-marketplace/products-mapping/composables/useProductList'
import StepIndicator from './StepIndicator.vue'
import BtnConnectProduct from '../integrasi-marketplace/products-mapping/BtnConnectProduct.vue'
import { SYNC_PRODUCT_STEP, SYNC_PRODUCT_STEP_BUNDLING, SYNC_PRODUCT_FIELDS } from './config'
import SyncInfoCard from './SyncInfoCard.vue'
import useVariantList from '../integrasi-marketplace/products-mapping/variants-mapping/composables/useVariantList'

export default {
  name: 'ModalSyncProduct',
  components: {
    StepIndicator,
    BtnConnectProduct,
    SyncInfoCard,
  },
  props: {
    shopId: {
      type: String,
      required: false,
      default: '',
    },
    warehouseId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      tableFields: SYNC_PRODUCT_FIELDS,
      type: 'product',
      isSyncing: false,
      item: {
        is_connected: false,
        is_synced: false,
        is_bundling: false,
        product_id: '',
        variant_id: '',
        variant_name: '-',
        product_name: '-',
        marketplace_product_id: '',
        marketplace_variant_id: '',
        warehouse_id: '',
      },
      searchedProduct: '',
      isMainProduct: false,
      mainProductsFetcher: null,
      subProductsFetcher: null,
      parentIds: [],
    }
  },
  computed: {
    stepSchema() {
      return (this.item.is_bundling && !this.item.is_variant) || !this.item.variant_name ? SYNC_PRODUCT_STEP_BUNDLING : SYNC_PRODUCT_STEP
    },
    tableData() {
      return this.type === 'product' ? this.productsFetcher?.data : this.variantsState?.data
    },
    shouldShowLoadMore() {
      if (this.type !== 'product') {
        return false
      }

      return (
        this.productsFetcher
        && this.productsFetcher.hasMore
      )
    },
    tableEmptyText() {
      if ((this.type === 'product' && this.productsFetcher.isLoading)
      || (this.type === 'variant' && this.variantsState.isLoading)) {
        return 'Memuat data...'
      }
      if (this.searchedProduct) {
        return `Tidak ada data yang ditemukan untuk "${this.searchedProduct}"`
      }
      return 'Tidak ada data yang ditemukan'
    },
    productsFetcher() {
      return this.isMainProduct ? this.mainProductsFetcher : this.subProductsFetcher
    },
  },
  created() {
    this.initializeComponent()
    this.initializeComposables()
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    showModal({
      type = 'product',
      data = {
        is_connected: false,
        is_synced: false,
        is_bundling: false,
        product_id: '',
        order_id: 0,
        variant_id: '',
        variant_name: '-',
        product_name: '-',
        marketplace: '',
        marketplace_product_id: '',
        marketplace_variant_id: '',
      },
    } = {}) {
      this.type = type
      this.item = data
      this.index = data.order_id

      if (data.isMainProduct !== this.isMainProduct) {
        this.isMainProduct = data.isMainProduct
      }

      if (type === 'product') {
        this.productsFetcher.fetchData({
          reset: true,
          options: {
            warehouseId: data.warehouse_id,
            is_variant: !data.variant_name ? 0 : 1,
          },
        })
      }
      if (type === 'variant') {
        this.switchToVariantStep({ selectedProductId: data.product_id, isBundleVariant: data.is_bundling && data.is_variant })
      }
      this.$refs.bModalSyncProduct.show()
      this.$nextTick(() => this.observeLoadingFooter())
    },
    onModalHidden() {
      this.cleanup()
    },
    hideModal() {
      this.cleanup()
      this.$refs.bModalSyncProduct.hide()
    },
    // Initialize state and fetcher
    initializeComponent() {
      const { createProductsFetcher: createMainProductsFetcher } = useProductList({
        http: this.$http_new,
        onError: message => this.$toast_error({ message }),
        endpoint: '/komship/api/v1/products/kompack/summaries',
      })

      // Fetcher for main products(first product in the list of marketplace order product)
      this.mainProductsFetcher = createMainProductsFetcher()

      const { createProductsFetcher } = useProductList({
        http: this.$http_new,
        onError: message => this.$toast_error({ message }),
        endpoint: '/komship/api/v1/product-warehouses/summaries',
      })

      // Fetcher for sub products(any products except first product in the list of marketplace order product)
      this.subProductsFetcher = createProductsFetcher()

      const {
        fetchVariantsData, state: variantsState, handleSearchInput, cleanupSearch,
      } = useVariantList(this)
      this.handleVariantSearchInput = handleSearchInput
      this.cleanupVariantSearch = cleanupSearch

      this.variantsState = variantsState
      this.fetchVariantsData = fetchVariantsData
    },
    initializeComposables() {
      const { handleSearchInput, cleanup: cleanupSearch } = useDebouncedSearch(
        this,
        keyword => {
          this.performSearch(keyword)
        },
        500,
      )
      this.handleSearchInput = handleSearchInput
      this.cleanupSearch = cleanupSearch

      const {
        setup: setupObserver,
        observe: observeElement,
        cleanup: cleanupObserver,
      } = useIntersectionObserver({
        onIntersect: entry => {
          if (
            entry.isIntersecting
            && this.productsFetcher
            && this.productsFetcher.hasMore
            && !this.productsFetcher.isLoading
          ) {
            this.productsFetcher.fetchData()
          }
        },
      })
      this.setupObserver = setupObserver
      this.observeElement = observeElement
      this.cleanupObserver = cleanupObserver
    },
    cleanup() {
      this.cleanupSearch()
      this.cleanupObserver()
      this.cleanupVariantSearch()
      this.searchedProduct = ''
      this.parentIds = []
    },
    observeLoadingFooter() {
      this.$nextTick(() => {
        const { loadingFooter } = this.$refs
        if (!loadingFooter || !this.productsFetcher) {
          return
        }

        if (this.observeElement) {
          this.observeElement(loadingFooter)
        } else if (this.setupObserver) {
          this.setupObserver(loadingFooter)
        }
      })
    },
    performSearch(keyword) {
      const fetchPromise = this.productsFetcher.fetchData({
        reset: true,
        options: {
          keyword: keyword || undefined,
          warehouseId: this.warehouseId,
          is_variant: this.item.variant_name ? 1 : 0,
        },
      })

      // Re-observe loading footer after fetch completes (jika ada)
      if (fetchPromise?.then) {
        fetchPromise.then(() => {
          this.$nextTick(() => {
            this.observeLoadingFooter()
          })
        })
      } else {
        this.$nextTick(() => {
          this.observeLoadingFooter()
        })
      }
    },
    handleSearch(keyword) {
      if (this.type === 'product') {
        this.handleSearchInput(keyword)
      } else {
        this.handleVariantSearchInput(keyword)
      }
    },
    async switchToVariantStep({ selectedProductId, isBundleVariant }) {
      await this.fetchVariantsData({ product_id: selectedProductId, url: isBundleVariant ? `/komship/api/v1/product-variant-bundles/${selectedProductId}/list` : undefined })
      this.type = 'variant'
      this.$nextTick(() => {
        if (!this.tableData) {
          return
        }
        this.$refs.stepIndicator.goToStep(1)
      })
    },
    async handleSyncAction(data) {
      this.isSyncing = true
      this.$refs[`btnConnectProduct-${data.index}`].setLoading(true)
      try {
        if (this.type === 'product') {
          await this.syncProductAction(data)
        } else {
          await this.syncVariantAction(data)
        }
      } finally {
        this.isSyncing = false
        this.$refs[`btnConnectProduct-${data.index}`].setLoading(false)
      }
    },
    async syncProductAction(data) {
      try {
        const payload = [{
          product_id: data.item.id,
          marketplace_product_id: this.item.marketplace_product_id,
        }]

        const { data: { data: { parent_ids } } } = await this.$http_new.post(
          `/komship/api/v1/marketplace-products/shops/${this.shopId}/sync`,
          payload,
        )

        this.parentIds = parent_ids

        this.$toast_success({
          message: 'Produk berhasil terhubung dengan marketplace',
        })

        this.item.product_id = data.item.id

        if (this.item.variant_name) {
          await this.switchToVariantStep({ selectedProductId: data.item.id, isBundleVariant: data.item.is_variant_bundle })

          return
        }

        this.$emit('success', {
          type: 'sync',
          index: this.index,
          product_id: data.item.id,
          order_id: this.index,
          variant_id: data.item.variant_id,
          marketplace: this.item.marketplace,
          product_name: data.item.name,
          is_main_product: this.isMainProduct,
        })
      } catch (error) {
        const isAlreadySynced = error.response?.data?.meta?.message.includes('already synced with another marketplace product')
        this.$toast_error({
          message: isAlreadySynced ? 'Produk ini sudah terhubung dengan produk marketplace lain' : 'Gagal menghubungkan produk dengan marketplace, silahkan coba lagi',
        })
      }
    },
    async syncVariantAction(data) {
      try {
        await this.$http_new.post(`/komship/api/v1/marketplace-product-variants/shops/${this.shopId}/sync`, {
          parent_id: this.parentIds[0] || this.item.id,
          variants: [{
            marketplace_variant_id: this.item.marketplace_variant_id,
            variant_id: data.item.variant_id || data.item.id,
          }],
        })

        this.$emit('success', {
          type: 'sync',
          index: this.index,
          order_id: this.index,
          product_id: this.item.product_id,
          variant_id: data.item.variant_id || data.item.id,
          variant_name: data.item.variant_name,
          marketplace: this.item.marketplace,
          is_main_product: this.isMainProduct,
        })
      } catch (error) {
        const isAlreadySynced = error.response?.data?.meta?.message.includes('already synced with another marketplace variant')
        this.$toast_error({
          message: isAlreadySynced
            ? 'Variasi produk ini sudah terhubung dengan variasi marketplace lain'
            : 'Gagal menghubungkan variasi produk dengan marketplace, silahkan coba lagi',
        })
      }
    },
  },
}
</script>
